import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './scss/TargetedMarketing.module.scss';

const ROW_CAP = 30;
const COLUMN_CAP = 8;

function cellText(value: unknown): string {
  if (value == null || value === '') {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function isPlainRow(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function ObjectTable({ rows }: { rows: Record<string, unknown>[] }): ReactElement {
  const { t } = useTranslation();
  const keys: string[] = [];
  rows.slice(0, ROW_CAP).forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!keys.includes(key)) {
        keys.push(key);
      }
    });
  });
  const columns = keys.slice(0, COLUMN_CAP);
  const shown = rows.slice(0, ROW_CAP);

  return (
    <>
      {rows.length > ROW_CAP ? (
        <p className={styles.muted}>
          {t('targetedMarketing.investigationStepTableCap', {
            shown: ROW_CAP,
            total: rows.length,
          })}
        </p>
      ) : null}
      {keys.length > COLUMN_CAP ? (
        <p className={styles.muted}>
          {t('targetedMarketing.investigationStepColumnCap', { count: COLUMN_CAP })}
        </p>
      ) : null}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, index) => (
              <tr key={String(row.id ?? row.name ?? index)}>
                {columns.map((column) => (
                  <td key={column}>{cellText(row[column])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StepValue({ value, depth }: { value: unknown, depth: number }): ReactElement {
  if (value == null || value === '') {
    return <p className={styles.muted}>—</p>;
  }
  if (depth > 4) {
    return <pre className={styles.stepJson}>{JSON.stringify(value, null, 2)}</pre>;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <p className={styles.placeholderBody}>{String(value)}</p>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <p className={styles.muted}>—</p>;
    }
    if (value.every(isPlainRow)) {
      return <ObjectTable rows={value} />;
    }
    return <pre className={styles.stepJson}>{JSON.stringify(value, null, 2)}</pre>;
  }
  if (isPlainRow(value)) {
    return (
      <>
        {Object.entries(value).map(([key, item]) => (
          <section key={key}>
            <h4 className={styles.sectionTitle}>{key}</h4>
            <StepValue value={item} depth={depth + 1} />
          </section>
        ))}
      </>
    );
  }
  return <pre className={styles.stepJson}>{JSON.stringify(value, null, 2)}</pre>;
}

function InvestigationStepResponse({ responseJson }: { responseJson: unknown }): ReactElement {
  const { t } = useTranslation();
  if (responseJson == null) {
    return <p className={styles.muted}>{t('targetedMarketing.investigationStepNoResponse')}</p>;
  }

  return (
    <div>
      <StepValue value={responseJson} depth={0} />
      <details>
        <summary>{t('targetedMarketing.investigationStepFullResponse')}</summary>
        <pre className={styles.stepJson}>{JSON.stringify(responseJson, null, 2)}</pre>
      </details>
    </div>
  );
}

export default InvestigationStepResponse;
